window.currency = (function () {
  var moneyFormat = "${{amount}}";

  function formatMoney(cents, format) {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    var value = "";
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || moneyFormat;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ",";
      decimal = decimal || ".";

      if (isNaN(number) || number === null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      var parts = number.split(".");
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : "";

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  return {
    formatMoney: formatMoney,
  };
})();

function handleEventListeners(events = [], type = "add") {
  const evts = Array.isArray(events) ? events : [events];
  for (const evt of evts) {
    if (!evt.el) continue;
    if (type === "add") {
      evt.el.addEventListener(evt.type, evt.callback);
    } else {
      evt.el.removeEventListener(evt.type, evt.callback);
    }
  }
}

function updateFilledState(event) {
  const field = event.target;

  if (event.type === "keyup" && field.matches("input, textarea")) {
    field.classList.toggle("is-filled", field.value !== "");
  }

  if (event.type === "change" && field.matches("select")) {
    field.parentNode.classList.toggle("is-filled", field.value !== "");
  }
}

document.addEventListener("keyup", updateFilledState);
document.addEventListener("change", updateFilledState);

class SiteHeader extends HTMLElement {
  constructor() {
    super();

    this.options = JSON.parse(this.getAttribute("data-section-settings"));
    this.useInlineNavigation = this.options.navigationLayout === "inline";
    this.isDesktopNavigationVisible = this.useInlineNavigation;
    this.isMobileNavigationOpen = false;
    this.openTrigger = window.matchMedia("(hover: hover) and (pointer: fine)").matches
      ? this.options.desktopOpenTrigger
      : "click";
    this.openItems = [];
    this.dropdownActivationTimeouts = {};
    this.dropdownDeactivationTimeouts = {};
    this.dropdownTimeout = 100;
    this.setupCssVariables = this.setupCssVariables.bind(this);
    this.clickHandler = this.clickHandler.bind(this);
    this.focusInHandler = this.focusInHandler.bind(this);
    this.focusOutHandler = this.focusOutHandler.bind(this);
    this.mouseOverHandler = this.mouseOverHandler.bind(this);
    this.mouseOutHandler = this.mouseOutHandler.bind(this);
    this.blockSelectHandler = this.blockSelectHandler.bind(this);
    this.blockDeselectHandler = this.blockDeselectHandler.bind(this);
    this.events = [
      {
        el: window,
        type: "resize",
        callback: this.setupCssVariables,
      },
      {
        el: document,
        type: "click",
        callback: this.clickHandler,
      },
      {
        el: this,
        type: "focusin",
        callback: this.focusInHandler,
      },
      {
        el: this,
        type: "focusout",
        callback: this.focusOutHandler,
      },
      {
        el: this,
        type: "mouseover",
        callback: this.mouseOverHandler,
      },
      {
        el: this,
        type: "mouseout",
        callback: this.mouseOutHandler,
      },
      {
        el: document,
        type: "shopify:block:select",
        callback: this.blockSelectHandler,
      },
      {
        el: document,
        type: "shopify:block:deselect",
        callback: this.blockDeselectHandler,
      },
    ];
  }

  connectedCallback() {
    this.desktopNavigationElement = this.querySelector(
      this.options.navigationLayout === "inline" ? ".nav-bar" : ".header__desktop-nav",
    );
    this.mobileNavigationElement = this.querySelector(".header__mobile-nav");
    this.allDesktopItems = this.desktopNavigationElement?.querySelectorAll(".nav-bar__item") || [];

    if (this.mobileNavigationElement) {
      this.mobileMenuElement = this.mobileNavigationElement.querySelector(".mobile-menu");
      this.mobileMenuToggleElement = this.mobileNavigationElement.querySelector(
        `[aria-controls="${this.mobileMenuElement.id}"]`,
      );
    }

    if (this.desktopNavigationElement && this.useInlineNavigation) {
      this.setupInlineNavigation();
    }

    this.setupCssVariables();
    handleEventListeners(this.events, "add");
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
    this.dropdownMenuObserver?.disconnect();
    document.body.classList.remove("no-mobile-scroll");
  }

  setupCssVariables() {
    document.documentElement.style.setProperty(
      "--header-height",
      `${this.parentNode.clientHeight}px`,
    );
    this.calculateMobileMenuMaxHeight();
  }

  clickHandler(event) {
    const target = event.target.closest("[data-action], [data-type=menuitem][aria-haspopup]");

    this.closeNavigationOnOutsideClick(event);

    if (!target) return;

    if (this.desktopNavigationElement?.contains(target)) {
      if (target.dataset.action === "toggle-menu") {
        this.toggleDesktopNavigation();
        return;
      }

      if (this.openTrigger === "click" && target.matches('[data-type="menuitem"][aria-haspopup]')) {
        this.toggleDropdown(event, target);
        return;
      }
    }

    if (this.mobileNavigationElement?.contains(target)) {
      if (target.dataset.action === "toggle-menu") {
        this.toggleMobileMenu();
        return;
      }

      if (target.dataset.action === "open-panel") {
        this.openMobilePanel(target);
        return;
      }

      if (target.dataset.action === "close-panel") {
        this.closeMobilePanel(target);
        return;
      }
    }
  }

  focusInHandler(event) {
    if (this.openTrigger !== "hover" || !this.desktopNavigationElement?.contains(event.target)) {
      return;
    }

    const target = event.target.closest('[data-type="menuitem"][aria-haspopup]');

    if (target) this.activateDropdown(event, target);
  }

  focusOutHandler(event) {
    if (!this.desktopNavigationElement?.contains(event.target)) return;

    if (event.relatedTarget && !this.desktopNavigationElement.contains(event.relatedTarget)) {
      this.closeDesktopNavigation();
    }

    if (this.openTrigger !== "hover") return;

    const target = event.target.closest(".is-dropdown-open");

    if (target) this.deactivateDropdown(event, target);
  }

  mouseOverHandler(event) {
    if (this.openTrigger !== "hover" || !this.desktopNavigationElement?.contains(event.target)) {
      return;
    }

    const menuItem = event.target.closest('[data-type="menuitem"][aria-haspopup]');

    if (menuItem) {
      this.activateDropdown(event, menuItem);
      return;
    }

    const openMenu = event.target.closest('[data-type="menu"][aria-hidden="false"]');

    if (openMenu) this.blockDropdownDeactivation(openMenu);
  }

  mouseOutHandler(event) {
    if (this.openTrigger !== "hover" || !this.desktopNavigationElement?.contains(event.target)) {
      return;
    }

    const target = event.target.closest(".is-dropdown-open");

    if (target) this.deactivateDropdown(event, target);
  }

  closeNavigationOnOutsideClick(event) {
    if (this.desktopNavigationElement && !this.desktopNavigationElement.contains(event.target)) {
      this.closeDesktopNavigation();
    }

    if (this.isMobileNavigationOpen && !this.mobileNavigationElement?.contains(event.target)) {
      this.toggleMobileMenu();
    }
  }

  openDesktopNavigation() {
    if (this.useInlineNavigation) return;

    this.desktopNavigationElement
      .querySelector('[data-action="toggle-menu"]')
      .setAttribute("aria-expanded", "true");
    this.desktopNavigationElement
      .querySelector('[data-type="menu"]')
      .setAttribute("aria-hidden", "false");
    this.isDesktopNavigationVisible = true;
  }

  closeDesktopNavigation() {
    if (!this.desktopNavigationElement) return;

    if (!this.useInlineNavigation) {
      this.desktopNavigationElement
        .querySelector('[data-action="toggle-menu"]')
        .setAttribute("aria-expanded", "false");
      this.desktopNavigationElement
        .querySelector('[data-type="menu"]')
        .setAttribute("aria-hidden", "true");
    }

    this.isDesktopNavigationVisible = this.useInlineNavigation;

    if (this.openTrigger === "click") {
      [...this.openItems].forEach((item) => this.deactivateDropdown({}, item));
    }
  }

  toggleDesktopNavigation() {
    if (this.isDesktopNavigationVisible) {
      this.closeDesktopNavigation();
    } else {
      this.openDesktopNavigation();
    }
  }

  toggleDropdown(event, target) {
    if (target.getAttribute("aria-expanded") === "false") event.preventDefault();

    if (target.getAttribute("aria-expanded") === "true") {
      if (target.getAttribute("href") === "#") {
        event.preventDefault();
        this.deactivateDropdown(event, target.closest(".is-dropdown-open"));
      }
    } else {
      this.activateDropdown(event, target);
    }
  }

  closeAllDropdowns() {
    this.allDesktopItems.forEach((item) => {
      if (!item.classList.contains("is-dropdown-open")) return;

      item.classList.remove("is-dropdown-open");
      item.querySelector('[data-type="menuitem"]').setAttribute("aria-expanded", "false");
      item.querySelector("[aria-hidden]").setAttribute("aria-hidden", "true");
    });
  }

  activateDropdown(event, target) {
    this.closeAllDropdowns();

    if (this.openTrigger === "click") {
      [...this.openItems].forEach((item) => {
        if (!item.contains(target)) this.deactivateDropdown(event, item);
      });
    }

    const menuToOpen = this.getSiblings(target, "[aria-hidden]")[0];

    if (!menuToOpen) return;

    for (const id in this.dropdownActivationTimeouts) {
      clearTimeout(this.dropdownActivationTimeouts[id]);
      delete this.dropdownActivationTimeouts[id];
    }

    target.setAttribute("aria-expanded", "true");
    target.parentNode.classList.add("is-dropdown-open");
    menuToOpen.setAttribute("aria-hidden", "false");

    if (this.openTrigger === "hover" && this.dropdownDeactivationTimeouts[menuToOpen.id]) {
      clearTimeout(this.dropdownDeactivationTimeouts[menuToOpen.id]);
      delete this.dropdownDeactivationTimeouts[menuToOpen.id];
    }

    if (this.useInlineNavigation) {
      const shouldOpenLeft = [...menuToOpen.querySelectorAll(".nav-dropdown")].some(
        (menu) => menu.getBoundingClientRect().right > window.innerWidth,
      );

      if (shouldOpenLeft) menuToOpen.classList.add("nav-dropdown--inverse");
    }

    target.closest('[data-type="menu"]').classList.add("nav-dropdown--glued");

    if (menuToOpen.classList.contains("mega-menu")) this.setupMegaMenu(menuToOpen);
    if (this.openTrigger === "click" && !this.openItems.includes(target.parentNode)) {
      this.openItems.push(target.parentNode);
    }
  }

  deactivateDropdown(event, target) {
    const relatedTarget = event.relatedTarget;

    if (this.openTrigger === "hover" && relatedTarget && target.contains(relatedTarget)) return;

    const menuToClose = target.querySelector("[aria-hidden]");

    if (!menuToClose) return;

    const close = () => {
      if (
        !relatedTarget?.classList?.contains("nav-bar__item") ||
        !relatedTarget.querySelector?.(".mega-menu")
      ) {
        target.classList.remove("is-dropdown-open");
        target.querySelector('[data-type="menuitem"]').setAttribute("aria-expanded", "false");
        menuToClose.setAttribute("aria-hidden", "true");
        target.closest('[data-type="menu"]')?.classList.remove("nav-dropdown--glued");
      }

      if (this.openTrigger === "click") {
        target.querySelectorAll(".is-dropdown-open").forEach((item) => {
          this.deactivateDropdown(event, item);
          this.openItems = this.openItems.filter((openItem) => openItem !== item);
        });
        this.openItems = this.openItems.filter((openItem) => openItem !== target);
      }
    };

    if (this.openTrigger === "click") {
      close();
    } else {
      this.dropdownDeactivationTimeouts[menuToClose.id] = setTimeout(() => {
        close();
        delete this.dropdownDeactivationTimeouts[menuToClose.id];
      }, this.dropdownTimeout);
    }
  }

  blockDropdownDeactivation(target) {
    if (this.dropdownDeactivationTimeouts[target.id] === undefined) return;

    clearTimeout(this.dropdownDeactivationTimeouts[target.id]);
    delete this.dropdownDeactivationTimeouts[target.id];

    for (const id in this.dropdownActivationTimeouts) {
      clearTimeout(this.dropdownActivationTimeouts[id]);
      delete this.dropdownActivationTimeouts[id];
    }
  }

  setupMegaMenu(megaMenu) {
    if (this.useInlineNavigation) return;

    const navDropdownWidth = megaMenu.closest(".nav-dropdown").clientWidth;
    megaMenu.style.maxWidth = `${Math.min(
      1400 - navDropdownWidth,
      window.innerWidth - navDropdownWidth - 80,
    )}px`;
  }

  setupInlineNavigation() {
    this.desktopNavigationElement.querySelectorAll(".mega-menu").forEach((megaMenu) => {
      megaMenu.closest(".nav-bar__item").classList.add("nav-bar__item--static");
    });

    if (!("MutationObserver" in window)) return;

    this.dropdownMenuObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.target.getAttribute("aria-hidden") === "false") {
          mutation.target.style.setProperty(
            "--distance-to-top",
            `${mutation.target.getBoundingClientRect().top}px`,
          );
        }
      });
    });

    this.desktopNavigationElement
      .querySelectorAll(".nav-dropdown .nav-dropdown")
      .forEach((item) => {
        this.dropdownMenuObserver.observe(item, {
          attributes: true,
          attributeFilter: ["aria-hidden"],
        });
      });
  }

  toggleMobileMenu() {
    this.isMobileNavigationOpen = !this.isMobileNavigationOpen;
    this.mobileMenuToggleElement.setAttribute(
      "aria-expanded",
      this.isMobileNavigationOpen ? "true" : "false",
    );
    this.mobileMenuElement.setAttribute(
      "aria-hidden",
      this.isMobileNavigationOpen ? "false" : "true",
    );

    if (this.isMobileNavigationOpen) {
      this.calculateMobileMenuMaxHeight();
      document.body.classList.add("no-mobile-scroll");
      return;
    }

    this.mobileMenuElement.style.maxHeight = "";
    this.mobileNavigationElement.querySelectorAll(".mobile-menu__panel.is-open").forEach((item) => {
      item.classList.remove("is-open");
    });
    document.body.classList.remove("no-mobile-scroll");
  }

  openMobilePanel(target) {
    target.setAttribute("aria-expanded", "true");
    this.mobileNavigationElement
      .querySelector(`#${CSS.escape(target.getAttribute("aria-controls"))}`)
      .classList.add("is-open");
  }

  closeMobilePanel(target) {
    const panelToClose = target.closest(".mobile-menu__panel.is-open");

    panelToClose.classList.remove("is-open");
    this.mobileNavigationElement
      .querySelector(`[aria-controls="${panelToClose.id}"]`)
      .setAttribute("aria-expanded", "false");
  }

  calculateMobileMenuMaxHeight() {
    if (!this.isMobileNavigationOpen) return;

    this.mobileMenuElement.style.maxHeight = `${
      window.innerHeight - this.querySelector(".header").getBoundingClientRect().bottom
    }px`;
  }

  getSiblings(element, selector) {
    return [...element.parentNode.children].filter(
      (sibling) => sibling !== element && sibling.matches(selector),
    );
  }

  blockSelectHandler(event) {
    if (!this.contains(event.target)) return;

    if (!this.useInlineNavigation) this.openDesktopNavigation();

    this.getSiblings(event.target.parentNode, ".is-dropdown-open").forEach((openItem) => {
      this.deactivateDropdown(event, openItem);
    });

    const menuItem = event.target.previousElementSibling;

    if (menuItem?.matches('[data-type="menuitem"][aria-haspopup]')) {
      this.activateDropdown(event, menuItem);
    }
  }

  blockDeselectHandler(event) {
    if (!this.contains(event.target)) return;

    if (!this.useInlineNavigation) this.closeDesktopNavigation();
    this.deactivateDropdown(event, event.target.parentNode);
  }
}

customElements.define("site-header", SiteHeader);

class ProductItem extends HTMLElement {
  constructor() {
    super();

    this.primary_image = this.querySelector(".product-card__image.media--primary img");
    this.product_links = this.querySelectorAll("a");
    this.quickBuyColorChangeHandler = this.quickBuyColorChangeHandler.bind(this);
    this.quickBuyVariantChangeHandler = this.quickBuyVariantChangeHandler.bind(this);
  }
  connectedCallback() {
    this.selected_mix_match =
      this.closest("product-recommendations")?.getAttribute("selected_mix_match");
    this.product_json = this.querySelector("[data-product-item-json]");
    if (!this.product_json) return;
    this.json = JSON.parse(this.product_json.innerHTML);
    if (this.getAttribute("hide-swatches") === "true") {
      this.calculateSwatches();
    }
    this.calculateSelects();
    this.addEventListeners();
    if (!this.selected_mix_match) return;
    this.initMixMatch();
  }
  addEventListeners() {
    this.addEventListener("input", this.inputHandler.bind(this));
    this.addEventListener("quick-buy:color-change", this.quickBuyColorChangeHandler);
    this.addEventListener("quick-buy:variant-change", this.quickBuyVariantChangeHandler);
    if (!this.selected_mix_match) return;
    document.addEventListener("mixmatch:change", this.updateMixMatch.bind(this));
    document.addEventListener("variant:change", this.captureVariantChange.bind(this));
  }
  inputHandler(e) {
    if (e.target.classList.contains("color-swatch__radio")) {
      this.updateSelectedSwatch(e.target);
      if (this.selected_mix_match) {
        document.dispatchEvent(
          new CustomEvent("mixmatch:change", {
            detail: {
              trigger: this,
              selected: this.selected_mix_match,
            },
          }),
        );
      }
    }
  }
  calculateSelects() {
    const in_stock_variants = this.json.variants.filter((variant) => {
      return variant.available;
    });
    const option1_in_stock_array = [];
    const option2_in_stock_array = [];
    in_stock_variants.forEach((variant) => {
      if (!option1_in_stock_array.includes(variant.option1)) {
        option1_in_stock_array.push(variant.option1);
      }
      if (!option2_in_stock_array.includes(variant.option2)) {
        option2_in_stock_array.push(variant.option2);
      }
    });
    const optionContainer = this.querySelector(".quick-buy__wrapper");
    if (!optionContainer) return;
    const optionPickers = optionContainer.querySelectorAll("[data-quick-buy-option-picker]");

    if (!optionPickers[0]) return;
    for (const input of optionPickers[0].querySelectorAll(".quick-buy__select-input")) {
      input.closest(".quick-buy__select-option").hidden = !option1_in_stock_array.includes(
        input.value,
      );
    }

    if (!optionPickers[1]) return;
    for (const input of optionPickers[1].querySelectorAll(".quick-buy__select-input")) {
      input.closest(".quick-buy__select-option").hidden = !option2_in_stock_array.includes(
        input.value,
      );
    }
  }
  calculateSwatches() {
    this.swatches = this.querySelectorAll(".color-swatch");
    if (!this.swatches) return;
    const color_option = this.json.options_with_values.filter((option) => {
      return option.name.toLowerCase() === "color" || option.name.toLowerCase() === "colour";
    });
    if (color_option.length <= 0) return;
    const color_position = color_option[0].position;
    const quickBuyColorPicker = this.querySelectorAll("[data-quick-buy-option-picker]")[
      color_position - 1
    ];
    const colors = {};
    color_option[0].values.forEach((color) => {
      const color_handle = color.toLowerCase().replace(" ", "_");
      colors[color_handle] = {
        color,
        count: 0,
      };
    });

    for (const variant of this.json.variants) {
      const color = variant[`option${color_position}`];
      const color_handle = color.toLowerCase().replace(" ", "_");
      colors[color_handle].count += variant.count;
    }
    for (const value of Object.values(colors)) {
      if (value.count <= 0) {
        this.querySelector(`[title="${value.color}"]`)?.parentElement.remove();
        Array.from(quickBuyColorPicker?.querySelectorAll(".quick-buy__select-input") || [])
          .find((input) => input.value === value.color)
          ?.closest(".quick-buy__select-option")
          ?.remove();
      }
    }
  }
  getColorOption() {
    return this.json.options_with_values.find((option) => {
      return option.name.toLowerCase() === "color" || option.name.toLowerCase() === "colour";
    });
  }
  getFirstVariantForColor(color) {
    const colorOption = this.getColorOption();
    if (!colorOption) return null;
    const variantOption = `option${colorOption.position}`;
    const colorVariants = this.json.variants.filter((variant) => variant[variantOption] === color);
    return colorVariants.find((variant) => variant.available) || colorVariants[0] || null;
  }
  selectColorSwatch(color) {
    const match = this.setColorSwatchChecked(color);
    if (!match) return;

    this.updateSelectedSwatch(match);
  }
  setColorSwatchChecked(color) {
    const swatches = Array.from(this.querySelectorAll(".color-swatch__radio"));
    const match = swatches.find((swatch) => swatch.value === color);
    if (!match) return null;

    for (const swatch of swatches) {
      swatch.removeAttribute("checked");
    }

    match.checked = true;
    match.setAttribute("checked", "checked");
    return match;
  }
  quickBuyColorChangeHandler(e) {
    const color = e.detail?.color;
    if (!color) return;
    this.setColorSwatchChecked(color);
  }
  quickBuyVariantChangeHandler(e) {
    const variantOption = e.detail?.variantOption;
    const color = e.detail?.color;

    if (color) this.setColorSwatchChecked(color);
    this.updateProductCardVariant(variantOption);
  }
  updateProductCardVariant(variantOption) {
    if (!variantOption) return;

    this.dataset.selectedVariantId = variantOption.value;

    if (variantOption.dataset.imageUrl && this.primary_image) {
      this.primary_image.src = variantOption.dataset.imageUrl;
      this.primary_image.srcset = "";
      this.primary_image.mediaId = variantOption.dataset.mediaId;
    }

    if (variantOption.dataset.variantUrl) {
      this.updateProductLinks(variantOption.dataset.variantUrl);
    }
  }
  updateProductLinks(variantUrl) {
    const variant_url = new URL(window.location.origin + variantUrl);
    const variant_search = new URLSearchParams(variant_url.search);
    const variant_id = variant_search.get("variant");

    if (!variant_id) return;

    for (const link of this.product_links) {
      if (link.href.includes(variant_url.pathname)) {
        const link_url = new URL(link.href);
        link_url.searchParams.set("variant", variant_id);
        link.href = link_url.toString().replace(link.origin, "");
      }
    }
  }
  updateSelectedSwatch(target) {
    const selectedVariant = this.getFirstVariantForColor(target.value);

    if (selectedVariant) this.dataset.selectedVariantId = selectedVariant.id;

    this.primary_image.src = target.dataset.imageUrl;
    this.primary_image.srcset = "";
    this.primary_image.mediaId = target.dataset.mediaId;
    this.updateProductLinks(target.dataset.variantUrl);
    this.dispatchEvent(
      new CustomEvent("product-card:swatch-change", {
        bubbles: true,
        detail: {
          color: target.value,
          variant: selectedVariant,
        },
      }),
    );
    if (!this.selected_mix_match) return;
    this.selected_mix_match = target.value;
  }
  initMixMatch() {
    const match = this.querySelector(`.color-swatch__radio[value="${this.selected_mix_match}"]`);
    if (!match) return;
    match.setAttribute("checked", "checked");
    this.updateSelectedSwatch(match);
  }
  updateMixMatch(e) {
    if (e.detail.trigger === this) return;
    const match = this.querySelector(`.color-swatch__radio[value="${e.detail.selected}"]`);
    if (!match) return;
    match.setAttribute("checked", "checked");
    this.updateSelectedSwatch(match);
  }
  captureVariantChange(e) {
    let color_option = null;
    for (const [index, option] of e.detail.options.entries()) {
      if (option === "Color" || option === "Colour" || option === "color" || option === "colour") {
        color_option = index;
        break;
      }
    }
    if (color_option === null) return;
    this.selectColorSwatch(e.detail.variant.options[color_option]);
  }
}
customElements.define("product-item", ProductItem);

class QuickBuy extends HTMLElement {
  strings = {
    cartAdd: "Add to cart",
    cartSuccess: "Added!",
    cartFail: "Error",
    noStock: "Sold out",
    chooseOptions: "Add to cart",
  };

  constructor() {
    super();

    this.strings.cartAdd = this.getAttribute("cart-add") || this.strings.cartAdd;
    this.strings.cartSuccess = this.getAttribute("cart-success") || this.strings.cartSuccess;
    this.strings.cartFail = this.getAttribute("cart-fail") || this.strings.cartFail;
    this.strings.noStock = this.getAttribute("no-stock") || this.strings.noStock;
    this.strings.chooseOptions = this.getAttribute("choose-options") || this.strings.chooseOptions;
    this.cartBubble = document.querySelector(".header__cart-count");

    this.form = this.querySelector(".cart-add__form");
    this.opener = this.querySelector("[data-action='open']");
    this.optionContainer = this.querySelector(".quick-buy__wrapper");
    this.optionOverlay = this.querySelector(".quick-buy__select-overlay");
    this.submitter = this.querySelector('button[data-action="submit"]');
    this.productItem = this.closest("product-item");
    this.clickHandler = this.clickHandler.bind(this);
    this.inputHandler = this.inputHandler.bind(this);
    this.keydownHandler = this.keydownHandler.bind(this);
    this.optionPickerClickHandler = this.optionPickerClickHandler.bind(this);
    this.dialogCloseHandler = this.dialogCloseHandler.bind(this);
    this.swatchChangeHandler = this.swatchChangeHandler.bind(this);
    this.optionPickers = [];
    this.activeOptionPicker = null;
    this.events = [
      {
        el: this,
        type: "click",
        callback: this.clickHandler,
      },
      {
        el: this,
        type: "input",
        callback: this.inputHandler,
      },
      {
        el: this,
        type: "keydown",
        callback: this.keydownHandler,
      },
      {
        el: document,
        type: "click",
        callback: this.optionPickerClickHandler,
      },
      {
        el: this.optionContainer,
        type: "close",
        callback: this.dialogCloseHandler,
      },
      {
        el: this.productItem,
        type: "product-card:swatch-change",
        callback: this.swatchChangeHandler,
      },
    ];
  }

  connectedCallback() {
    this.setupOptionPickers();
    handleEventListeners(this.events, "add");
  }

  disconnectedCallback() {
    this.closeOptionPicker();
    if (this.optionContainer?.open) this.optionContainer.close();
    handleEventListeners(this.events, "remove");
  }

  setupOptionPickers() {
    this.optionPickers = Array.from(this.querySelectorAll("[data-quick-buy-option-picker]"));

    this.optionPickers.forEach((optionPicker) => {
      const button = optionPicker.querySelector(".quick-buy__select-button");
      const menu = optionPicker.querySelector(".quick-buy__select-menu");
      const inputs = Array.from(optionPicker.querySelectorAll(".quick-buy__select-input"));

      if (!button || !menu || !inputs.length) return;

      optionPicker.button = button;
      optionPicker.menu = menu;
      this.syncOptionPicker(optionPicker);
    });
  }

  toggleOptionPicker(optionPicker) {
    if (this.activeOptionPicker === optionPicker) {
      this.closeOptionPicker();
      return;
    }

    this.openOptionPicker(optionPicker);
  }

  openOptionPicker(optionPicker) {
    if (!optionPicker?.menu || !optionPicker?.button) return;

    this.closeOptionPicker();
    this.activeOptionPicker = optionPicker;

    if (!optionPicker.menu.open) optionPicker.menu.show();
    this.optionOverlay?.classList.add("is-open");
    optionPicker.button.setAttribute("aria-expanded", "true");

    optionPicker.querySelector(".quick-buy__select-input:checked")?.focus();
  }

  closeOptionPicker() {
    const optionPicker = this.activeOptionPicker;
    if (!optionPicker?.menu) return;

    this.optionOverlay?.classList.remove("is-open");
    if (optionPicker.menu.open) optionPicker.menu.close();
    optionPicker.button?.setAttribute("aria-expanded", "false");

    this.activeOptionPicker = null;
  }

  optionPickerClickHandler(event) {
    const optionPicker = this.activeOptionPicker;
    if (optionPicker) {
      if (optionPicker.contains(event.target)) return;

      this.closeOptionPicker();
      return;
    }

    if (!this.isOpen() || this.optionContainer.matches(":modal")) return;

    const clickInsideOpener = this.opener?.contains(event.target);
    const clickInsidePanel = this.optionContainer
      ?.querySelector(".quick-buy__panel")
      ?.contains(event.target);

    if (clickInsideOpener || clickInsidePanel) return;

    this.close();
  }

  syncOptionPicker(optionPicker) {
    const selectedInput = optionPicker.querySelector(".quick-buy__select-input:checked");
    const selectedOption = selectedInput?.closest(".quick-buy__select-option");
    if (!selectedOption) return;

    const selectedSwatch = selectedOption.querySelector(".quick-buy__select-swatch");
    const buttonSwatch = optionPicker.button.querySelector(".quick-buy__select-swatch");
    const selectedText = selectedOption.querySelector(".quick-buy__select-text");
    if (selectedSwatch && buttonSwatch) {
      buttonSwatch.replaceWith(selectedSwatch.cloneNode(true));
    }
    optionPicker.button.querySelector(".quick-buy__select-text").textContent =
      selectedText.textContent;
  }

  inputHandler(e) {
    const optionInput = e.target.closest(".quick-buy__select-input");
    const optionPicker = optionInput?.closest("[data-quick-buy-option-picker]");
    if (optionPicker) {
      this.syncOptionPicker(optionPicker);
      this.closeOptionPicker();
      optionPicker.button?.focus();
    }

    if (!this.form) return;
    const selectedInputs = this.optionPickers
      .map((optionPicker) => optionPicker.querySelector(".quick-buy__select-input:checked"))
      .filter(Boolean);
    if (selectedInputs.length !== this.optionPickers.length) return;

    const selectedVariantTitle = selectedInputs.map((input) => input.value).join(" / ");
    const matchedVariant = Array.from(this.form.querySelectorAll('select[name="id"] option')).find(
      (option) => option.dataset.title === selectedVariantTitle,
    );
    this.selectVariant(matchedVariant);
    this.dispatchVariantChange(matchedVariant, selectedInputs);
    this.dispatchColorChange(e.target, selectedInputs);
  }

  dispatchVariantChange(matchedVariant, selectedInputs) {
    if (!this.productItem) return;

    const colorOption = this.productItem.getColorOption?.();
    const colorInput = colorOption ? selectedInputs[colorOption.position - 1] : null;

    this.dispatchEvent(
      new CustomEvent("quick-buy:variant-change", {
        bubbles: true,
        detail: {
          color: colorInput?.value,
          variantOption: matchedVariant,
        },
      }),
    );
  }

  dispatchColorChange(target, selectedInputs) {
    if (!this.productItem || !target.matches(".quick-buy__select-input")) return;
    const colorOption = this.productItem.getColorOption?.();
    if (!colorOption) return;
    const colorInput = selectedInputs[colorOption.position - 1];
    if (target !== colorInput) return;

    this.dispatchEvent(
      new CustomEvent("quick-buy:color-change", {
        bubbles: true,
        detail: {
          color: target.value,
          variant: this.productItem.getFirstVariantForColor?.(target.value),
        },
      }),
    );
  }

  swatchChangeHandler(e) {
    const variant = e.detail?.variant;
    if (!variant) return;

    if (!this.optionPickers.length) {
      const masterVariant = this.form?.querySelector(`[value="${variant.id}"]`);
      this.selectVariant(masterVariant);
      return;
    }

    this.optionPickers.forEach((optionPicker, index) => {
      const value = variant[`option${index + 1}`];
      if (!value) return;
      const input = Array.from(optionPicker.querySelectorAll(".quick-buy__select-input")).find(
        (optionInput) => optionInput.value === value,
      );
      if (input) input.checked = true;
      this.syncOptionPicker(optionPicker);
    });

    const masterVariant = this.form?.querySelector(`[value="${variant.id}"]`);
    this.selectVariant(masterVariant);
  }

  selectVariant(matchedVariant) {
    if (!this.form) return;
    const masterSelect = this.form.querySelector('select[name="id"]');
    if (!masterSelect) return;

    if (matchedVariant) {
      masterSelect.value = matchedVariant.value;
    }
    this.form.querySelectorAll("option").forEach((option) => {
      option.removeAttribute("selected");
    });
    matchedVariant?.setAttribute("selected", "selected");
    this.checkAvailability(matchedVariant);
  }

  getSelectedVariant() {
    return this.form?.querySelector('select[name="id"]')?.selectedOptions[0] || null;
  }

  clickHandler(e) {
    const optionPickerButton = e.target.closest(".quick-buy__select-button");
    if (optionPickerButton) {
      e.preventDefault();
      this.toggleOptionPicker(optionPickerButton.closest("[data-quick-buy-option-picker]"));
      return;
    }

    if (e.target === this.optionContainer && this.optionContainer.matches(":modal")) {
      this.close();
      return;
    }

    const actionElement = e.target.closest("[data-action]");
    if (!actionElement) return;

    e.preventDefault();
    if (actionElement.dataset.action === "open") {
      this.open();
    }
    if (actionElement.dataset.action === "close") {
      this.close();
    }
    if (actionElement.dataset.action === "close-select") {
      e.stopPropagation();
      const button = this.activeOptionPicker?.button;
      this.closeOptionPicker();
      button?.focus();
    }
    if (actionElement.dataset.action === "submit") {
      this.submit();
    }
  }

  keydownHandler(e) {
    const optionPickerButton = e.target.closest(".quick-buy__select-button");
    if (optionPickerButton && ["ArrowDown", "ArrowUp"].includes(e.key)) {
      e.preventDefault();
      this.openOptionPicker(optionPickerButton.closest("[data-quick-buy-option-picker]"));
      return;
    }

    const optionPickerMenu = e.target.closest(".quick-buy__select-menu");
    if (!optionPickerMenu || e.key !== "Escape") return;

    e.preventDefault();
    e.stopPropagation();
    const button = this.activeOptionPicker?.button;
    this.closeOptionPicker();
    button?.focus();
  }

  isOpen() {
    return Boolean(this.optionContainer?.open);
  }

  open() {
    if (!this.optionContainer || this.optionContainer.open) return;

    this.shouldUseModal() ? this.optionContainer.showModal() : this.optionContainer.show();
    this.opener?.setAttribute("aria-expanded", "true");
    this.productItem?.classList.add("product-item--quick-buy-active");
  }

  close() {
    this.closeOptionPicker();
    if (this.optionContainer?.open) this.optionContainer.close();
  }

  dialogCloseHandler() {
    this.closeOptionPicker();
    this.opener?.setAttribute("aria-expanded", "false");
    this.productItem?.classList.remove("product-item--quick-buy-active");
  }

  shouldUseModal() {
    return window.matchMedia("(max-width: 767px)").matches;
  }

  async submit() {
    if (!this.form || !this.submitter || this.submitter.disabled) return;

    const formData = new FormData(this.form);
    for (const key of Array.from(formData.keys())) {
      if (key.startsWith("quick-buy-")) formData.delete(key);
    }
    let added = false;
    this.submitter.disabled = true;
    this.submitter.classList.add("btn--loading");
    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHTTPRequest",
        },
        body: formData,
      });
      if (!response.ok) {
        throw new Error("[CART_ADD]: There was an error adding an item to the cart");
      }
      const data = await response.json();
      this.submitter.textContent = this.strings.cartSuccess;
      this.submitter.classList.add("btn--success");
      added = true;
    } catch (error) {
      console.log(error);
      this.submitter.textContent = this.strings.cartFail;
      this.submitter.classList.add("btn--error");
    } finally {
      this.submitter.classList.remove("btn--loading");
      setTimeout(() => {
        this.submitter.classList.remove("btn--success", "btn--error");
        this.checkAvailability(this.getSelectedVariant());
      }, 2000);
    }
    if (added) await this.updateCartCount();
  }

  async updateCartCount() {
    try {
      const response = await fetch("/cart.js");
      if (!response.ok) {
        throw new Error("Something went wrong");
      }
      const data = await response.json();
      this.cartBubble.innerText = data.item_count;
      if (data.item_count < 1) {
        this.cartBubble.classList.add("cart-empty");
      } else {
        this.cartBubble.classList.remove("cart-empty");
      }
      document.dispatchEvent(new CustomEvent("cart:updated", { detail: { data } }));
    } catch (error) {
      console.log(error);
    }
  }

  checkAvailability(variant) {
    if (!this.submitter) return;
    let available = false;
    if (variant) {
      available = variant.dataset.available === "false" ? false : true;
    }
    this.submitter.classList.toggle("btn--disabled", !available);
    this.submitter.disabled = !available;
    !available
      ? (this.submitter.textContent = this.strings.noStock)
      : (this.submitter.textContent = this.strings.cartAdd);
  }
}

customElements.define("quick-buy", QuickBuy);

class SiteSearch extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector("#site-search-dialog");
    this.search_button = this.querySelector('[data-action="open"]');
    this.close_button = this.querySelector('.site-search__close[data-action="close"]');
    this.search_height = this.getBoundingClientRect().height;
    this.input = this.querySelector('form input[type="search"]');
    // Prevent bfcache from prefilling search
    window.addEventListener("pageshow", (e) => {
      if (e.persisted) {
        this.dialog.close();
        this.input.value = "";
        this.input.dispatchEvent(new Event("input"));
      }
    });
    this.results_container = this.querySelector(".site-search__results-container");

    this.search = {
      query: this.getAttribute("search-query"),
      type: this.dataset.searchType,
      limit: this.dataset.maxResults,
      scope: "each",
      fields: this.dataset.searchFields,
      unavailable: this.dataset.unavailableProducts,
    };

    this.events = [
      {
        el: this,
        type: "click",
        callback: this.clickHandler.bind(this),
      },
      {
        el: this.input,
        type: "input",
        callback: this.inputHandler.bind(this),
      },
      {
        el: document,
        type: "keydown",
        callback: this.keyboardHandler.bind(this),
      },
    ];
  }

  connectedCallback() {
    this.classList.remove("search-loading");
    // this.input.closest(".site-search__input-container").style.height = this.search_height + "px";
    if (this.dataset.animatePlaceholder) {
      this.animatePlaceholder();
    }
    handleEventListeners(this.events);
  }

  clickHandler(e) {
    if (this.search_button.contains(e.target)) {
      this.dialog.show();
    }
    if (e.target.closest('[data-action="close"]')) {
      this.dialog.close();
    }
  }

  keyboardHandler(e) {
    if (e.key === "Escape") {
      this.dialog.close();
    }
  }
  inputHandler(e) {
    this.debounce(() => this.setSearchQuery(e));
  }

  setSearchQuery(e) {
    this.setAttribute("search-query", e.target.value);
  }

  attributeChangedCallback(name, prev, next) {
    if (prev === next) return;
    if (next === "") {
      this.querySelector("[data-results]").dataset.results = "false";
    } else {
      this.fetchSearchResults(next);
    }
  }

  static get observedAttributes() {
    return ["search-query"];
  }

  async fetchSearchResults(query) {
    console.log(query);
    this.results_container.classList.add("results--loading");
    const string = `/search/suggest?q=${query}&resources[type]=${this.search.type}&resources[limit]=${this.search.limit}&resources[limit_scope]=${this.search.scope}&resources[options][fields]=${this.search.fields}&resources[options][unavailable_products]=${this.search.unavailable}&section_id=site-predictive-search`;
    console.log(string);
    try {
      const response = await fetch(string);
      if (!response.ok) {
        throw new Error(response.status);
      }
      const data = await response.text();
      const results = new DOMParser()
        .parseFromString(data, "text/html")
        .querySelector("#shopify-section-site-predictive-search");
      this.renderSearchResults(results);
    } catch (err) {
      console.log(err);
    } finally {
      this.results_container.classList.remove("results--loading");
    }
  }

  renderSearchResults(html) {
    const no_results = html.querySelector(".column--noresults");
    if (no_results) {
      this.querySelector(".column--noresults").innerHTML =
        html.querySelector(".column--noresults").innerHTML;
      this.querySelector("[data-results]").dataset.results = "none";
    } else {
      if (html.querySelector(".column--links")) {
        this.querySelector(".column--links").innerHTML =
          html.querySelector(".column--links").innerHTML;
      } else {
        this.querySelector(".column--links").innerHTML = "";
      }
      if (html.querySelector(".column--products")) {
        this.querySelector(".column--products").innerHTML =
          html.querySelector(".column--products").innerHTML;
      } else {
        this.querySelector(".column--products").innerHTML = "";
      }
      this.querySelector("[data-results]").dataset.results = "true";
    }
  }

  animatePlaceholder() {
    const placeholder_container = this.querySelector("[data-placeholder-strings]");

    if (placeholder_container) {
      const placeholder_values = placeholder_container.dataset.placeholderStrings.split("|||");
      if (placeholder_values.length > 1) {
        this.animation_string_index = 0;
        this.animation_value_index = placeholder_values[this.animation_string_index].length;
        this.delete = true;
        this.animation_speed = +this.dataset.animationSpeed || 100;
        this.delete_speed = +this.dataset.deleteSpeed || 50;
        this.animation_delay = +this.dataset.animationDelay || 1000;
        console.log(this.animation_speed, this.delete_speed, this.animation_delay);
        setTimeout(() => {
          this.typingEffect(placeholder_container, placeholder_values);
        }, this.animation_delay);
      }
    }
  }

  typingEffect(container, values) {
    const current_string = values[this.animation_string_index];
    let pause = false;
    if (this.delete && this.animation_value_index >= 0) {
      container.innerHTML = current_string.substr(0, this.animation_value_index);
      this.animation_value_index--;
    } else if (this.animation_value_index === -1) {
      this.animation_string_index + 1 >= values.length
        ? (this.animation_string_index = 0)
        : this.animation_string_index++;
      this.delete = !this.delete;
      this.animation_value_index = 0;
    } else if (this.animation_value_index < current_string.length) {
      container.innerHTML = current_string.substr(0, this.animation_value_index + 1);
      this.animation_value_index++;
    } else {
      pause = true;
      this.delete = !this.delete;
    }
    setTimeout(
      () => {
        this.typingEffect(container, values);
      },
      pause ? this.animation_delay : this.delete ? this.delete_speed : this.animation_speed,
    );
  }

  debounce(fn, wait = 500) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      fn.apply(this);
    }, wait);
  }
}
customElements.define("site-search", SiteSearch);

class SearchHistory extends HTMLElement {
  constructor() {
    super();

    this.search_history = localStorage.getItem("search-history")
      ? JSON.parse(localStorage.getItem("search-history"))
      : [];
  }

  connectedCallback() {
    if (this.search_history.length > 0) {
      this.renderSearchHistory();
    }
    const query = new URLSearchParams(window.location.search).get("q");
    if (!query) return;

    const ind = this.search_history.findIndex((value) => value.query === query);
    if (ind === -1) {
      this.storeQuery(query);
    } else if (ind > 0) {
      this.search_history.unshift(this.search_history.splice(ind, 1)[0]);
      localStorage.setItem("search-history", JSON.stringify(this.search_history));
    }
  }
  async storeQuery(q) {
    const string = `/search/suggest.json?q=${q}&resources[type]=product&resources[limit]=4&resources[options][fields]=title,product_type,variants.title,variants.sku,variants.barcode,tag`;

    try {
      const response = await fetch(string);
      if (!response.ok) {
        throw new Error(response.status);
      }
      const data = await response.json();
      if (data.resources?.results?.products?.length > 0) {
        this.search_history.unshift({
          query: q,
          products: data.resources.results.products,
        });
        if (this.search_history.length >= +this.dataset.maxProducts - 1) {
          this.search_history = this.search_history.splice(0, +this.dataset.maxProducts);
        }
      }
    } catch (err) {
      console.log(err);
    }

    localStorage.setItem("search-history", JSON.stringify(this.search_history));
  }
  renderSearchHistory() {
    const container = this.querySelector(".site-predictive-search__list");
    for (const item of this.search_history) {
      const li = document.createElement("li");
      li.classList.add("site-predictive-search__list-item");
      const link = document.createElement("a");
      link.href = `/search?q=${item.query}&type=product`;
      link.classList.add("search-history__item");
      const img_grid = document.createElement("div");
      img_grid.classList.add("search-history__images");
      for (const prod of item.products) {
        const img = document.createElement("img");
        img.classList.add("search-history__image");
        img.src = prod.image + "&width=300";
        img_grid.appendChild(img);
      }
      const title = document.createElement("span");
      title.innerText = item.query;

      link.appendChild(img_grid);
      link.appendChild(title);
      li.appendChild(link);
      container.appendChild(li);
    }
    this.classList.add("search-history--loaded");
  }
}
customElements.define("search-history", SearchHistory);

class SearchRecentlyViewed extends HTMLElement {
  constructor() {
    super();
    this.recentlyViewedHandles = JSON.parse(localStorage.getItem("recentlyViewedItems"));
    this.currentProduct = this.getAttribute("current-product");

    if (this.currentProduct && this.recentlyViewedHandles) {
      this.recentlyViewedHandles = this.recentlyViewedHandles.filter((handle) => {
        return handle != this.currentProduct;
      });
    }
  }

  connectedCallback() {
    this.addEventListeners();
  }

  addEventListeners() {
    if (!this.recentlyViewedHandles) return;
    document.addEventListener("DOMContentLoaded", this.fetchRecentlyViewed.bind(this));
  }

  async fetchRecentlyViewed() {
    if (!this.recentlyViewedHandles) return;

    try {
      const res = await fetch(
        `/search?view=search-recently-viewed-items&q=${this.recentlyViewedHandles.join(",")}`,
      );
      const data = await res.text();
      if (!data) return;
      this.renderRecentlyViewed(data);
    } catch (error) {
      console.log(error);
    }
  }

  renderRecentlyViewed(data) {
    const items = new DOMParser()
      .parseFromString(data, "text/html")
      .querySelector(".site-predictive-search__list").innerHTML;
    const item_wrapper = this.querySelector(".site-predictive-search__list");
    item_wrapper.innerHTML = items;
    this.classList.add("recently-viewed--loaded");
  }
}
customElements.define("search-recently-viewed", SearchRecentlyViewed);

class QuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.quantityInput = this.querySelector(".quantity-selector__input");
    this.decrease = this.querySelector('.quantity-selector__button[data-change="decrease"]');
    this.increase = this.querySelector('.quantity-selector__button[data-change="increase"]');
  }

  connectedCallback() {
    this.addEventListeners();
  }

  addEventListeners() {
    this.addEventListener("click", this.adjustQuantity.bind(this));
  }

  adjustQuantity(e) {
    if (
      e.target === this.decrease ||
      e.target.closest(".quantity-selector__button") === this.decrease
    ) {
      if (+this.quantityInput.value <= 1) return;
      +this.quantityInput.value--;
    }
    if (
      e.target === this.increase ||
      e.target.closest(".quantity-selector__button") === this.increase
    ) {
      +this.quantityInput.value++;
    }
  }
}
customElements.define("quantity-selector", QuantitySelector);

class MainCarousel extends HTMLElement {
  constructor() {
    super();

    this.is_visible = false;

    this.defaultOptions = {
      loop: true,
    };

    this.elements = {
      swiper: this.querySelector(".main-carousel"),
      pagination: this.querySelector(".main-carousel__pagination"),
      prevButton: this.querySelector(".main-carousel__button-prev"),
      nextButton: this.querySelector(".main-carousel__button-next"),
    };

    this.swiperOptions = {
      ...this.defaultOptions,
    };
    if (this.elements.swiper.dataset.carouselOptions) {
      this.swiperOptions = {
        ...this.defaultOptions,
        ...JSON.parse(this.elements.swiper.dataset.carouselOptions),
      };
    }

    if (this.elements.prevButton && this.elements.nextButton) {
      this.swiperOptions.navigation = {
        nextEl: this.elements.nextButton,
        prevEl: this.elements.prevButton,
      };
      this.elements.swiper.dataset.alignArrows
        ? (this.align_arrows = JSON.parse(this.elements.swiper.dataset.alignArrows))
        : null;
    }

    if (this.elements.pagination) {
      this.swiperOptions.pagination = {
        el: this.elements.pagination,
      };
    }
  }

  connectedCallback() {
    this.addEventListeners();
    this.checkVisibility();
  }

  addEventListeners() {
    window.addEventListener("resize", this.positionArrows.bind(this));
    window.addEventListener("scroll", this.checkVisibility.bind(this));
  }

  checkVisibility() {
    if (this.is_visible) return;
    const rect = this.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      if (!this.is_visible) {
        this.init();
      }
      this.is_visible = true;
    }
  }

  init() {
    if (!this.elements.swiper) return;
    this.swiper = new Swiper(this.elements.swiper, this.swiperOptions);
    this.positionArrows();
  }

  positionArrows() {
    if (!this.elements.swiper) return;
    setTimeout(() => {
      if (!this.align_arrows || !this.elements.prevButton || !this.elements.nextButton) return;
      const slide_image_height = this.swiper.slidesEl.querySelector(".luc-media")?.offsetHeight;
      this.elements.nextButton.style.top = slide_image_height / 2 + "px";
      this.elements.nextButton.style.marginTop = -this.elements.nextButton.offsetHeight / 2 + "px";
      this.elements.prevButton.style.top = slide_image_height / 2 + "px";
      this.elements.prevButton.style.marginTop = -this.elements.prevButton.offsetHeight / 2 + "px";
    }, 1);
  }
}
customElements.define("main-carousel", MainCarousel);

class RecentlyViewedCarousel extends MainCarousel {
  constructor() {
    super();
    this.recentlyViewedHandles = JSON.parse(localStorage.getItem("recentlyViewedItems"));
    this.currentProduct = this.getAttribute("current-product");

    if (this.currentProduct && this.recentlyViewedHandles) {
      this.recentlyViewedHandles = this.recentlyViewedHandles.filter((handle) => {
        return handle != this.currentProduct;
      });
    }
  }

  connectedCallback() {
    this.addEventListeners();
  }

  addEventListeners() {
    document.addEventListener("DOMContentLoaded", this.fetchRecentlyViewed.bind(this));
  }

  async fetchRecentlyViewed() {
    if (!this.recentlyViewedHandles) return;

    try {
      const res = await fetch(
        `/search?view=recently-viewed-items&q=${this.recentlyViewedHandles.join(",")}`,
      );
      const data = await res.text();
      if (!data) return;
      this.renderRecentlyViewed(data);
    } catch (error) {
      console.log(error);
    }
  }

  renderRecentlyViewed(data) {
    const items = new DOMParser()
      .parseFromString(data, "text/html")
      .querySelector(".main-carousel__wrapper").innerHTML;
    const item_wrapper = this.querySelector(".main-carousel__wrapper");
    item_wrapper.innerHTML = items;
    this.init();
  }
}
customElements.define("recently-viewed-carousel", RecentlyViewedCarousel);

class VideoSection extends HTMLElement {
  constructor() {
    super();
    this.events = [
      {
        el: this,
        type: "click",
        callback: this.clickHandler.bind(this),
      },
    ];
  }

  connectedCallback() {
    handleEventListeners(this.events, "add");
  }
  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
  }

  clickHandler(e) {
    if (e.currentTarget.dataset.action === "play-video") {
      const iframe = e.currentTarget.querySelector("iframe");
      iframe.src = iframe.getAttribute("data-src");
      e.currentTarget.classList.add("is-playing");
    }
  }
}

customElements.define("video-section", VideoSection);

class ValuePicker extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.clickHandler = this.clickHandler.bind(this);
    this.focusOutHandler = this.focusOutHandler.bind(this);
    this.events = [
      {
        el: document,
        type: "click",
        callback: this.clickHandler,
      },
      {
        el: this,
        type: "focusout",
        callback: this.focusOutHandler,
      },
    ];
  }

  connectedCallback() {
    this.classList.add("value-picker");
    this.togglerElement = document.querySelector(
      `[data-action="open-value-picker"][aria-controls="${this.id}"]`,
    );
    handleEventListeners(this.events, "add");
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
  }

  clickHandler(event) {
    const openButton = event.target.closest('[data-action="open-value-picker"]');
    const closeButton = event.target.closest('[data-action="close-value-picker"]');
    const valueButton = event.target.closest('[data-action="select-value"]');

    if (openButton && openButton.getAttribute("aria-controls") === this.id) {
      this.toggle(event);
      return;
    }

    if (closeButton && closeButton.getAttribute("aria-controls") === this.id) {
      this.toggle(event);
      return;
    }

    if (valueButton && this.contains(valueButton)) {
      this.selectValue(valueButton);
      return;
    }

    this.detectOutsideClick(event);
  }

  focusOutHandler(event) {
    if (!this.contains(event.relatedTarget)) {
      this.close();
    }
  }

  toggle(event) {
    if (this.isOpen) {
      this.close(event);
    } else {
      this.open(event);
    }
  }

  open() {
    this.getTogglerElement()?.setAttribute("aria-expanded", "true");
    this.setAttribute("aria-hidden", "false");

    if (window.matchMedia("screen and (max-width: 640px)").matches) {
      const headerElement = document.querySelector(".shopify-section__header");

      if (headerElement) {
        headerElement.style.zIndex = "3";
      }
    }

    this.isOpen = true;
    document.body.classList.add("no-mobile-scroll");
  }

  close() {
    this.getTogglerElement()?.setAttribute("aria-expanded", "false");
    this.setAttribute("aria-hidden", "true");

    const headerElement = document.querySelector(".shopify-section__header");

    if (headerElement) {
      headerElement.style.zIndex = "";
    }

    this.isOpen = false;
    document.body.classList.remove("no-mobile-scroll");
  }

  selectValue(target) {
    this.dispatchEvent(
      new CustomEvent("value:select", {
        bubbles: true,
        detail: {
          value: target.getAttribute("data-value"),
        },
      }),
    );
    this.close();
  }

  detectOutsideClick(event) {
    if (
      !this.isOpen ||
      this.getTogglerElement() === event.target ||
      this.getTogglerElement()?.contains(event.target)
    ) {
      return;
    }

    if (!event.target.closest(".value-picker__inner")) {
      this.close(event);
    }
  }

  getTogglerElement() {
    if (!this.togglerElement || !document.documentElement.contains(this.togglerElement)) {
      this.togglerElement = document.querySelector(
        `[data-action="open-value-picker"][aria-controls="${this.id}"]`,
      );
    }

    return this.togglerElement;
  }
}

customElements.define("value-picker", ValuePicker);

class MainCart extends HTMLElement {
  constructor() {
    super();

    this.itemCount = parseInt(this.getAttribute("data-item-count")) || 0;
    this.keyboardHandler = this.keyboardHandler.bind(this);
    this.cartEventHandler = this.cartEventHandler.bind(this);
    this.events = [
      {
        el: this,
        type: "click",
        callback: this.clickHandler.bind(this),
      },
      {
        el: this,
        type: "change",
        callback: this.changeHandler.bind(this),
      },
      {
        el: this,
        type: "keyup",
        callback: this.keyboardHandler,
      },
      {
        el: this,
        type: "keydown",
        callback: this.keyboardHandler,
      },
      {
        el: document.documentElement,
        type: "product:added",
        callback: this.cartEventHandler,
      },
      {
        el: document.documentElement,
        type: "cart:refresh",
        callback: this.cartEventHandler,
      },
    ];
  }

  connectedCallback() {
    handleEventListeners(this.events, "add");
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
  }

  clickHandler(event) {
    const quantityControl = event.target.closest(
      '[data-action="decrease-quantity"], [data-action="increase-quantity"]',
    );

    if (quantityControl) {
      this._updateQuantity(event, quantityControl);
    }
  }

  async changeHandler(event) {
    const cartNoteCheckbox = event.target.closest(".cart-recap__note-checkbox");
    const quantityInput = event.target.closest(
      ".quantity-selector:not(.quantity-selector--product) .quantity-selector__value",
    );

    if (cartNoteCheckbox) {
      await this._updateCartNote(cartNoteCheckbox);
    }

    if (quantityInput) {
      this._updateQuantity(event, quantityInput);
    }
  }

  async _updateCartNote(cartNoteCheckbox) {
    const noteInput = cartNoteCheckbox.closest(".cart-recap__note").querySelector('[name="note"]');

    if (!noteInput) {
      return;
    }

    noteInput.disabled = !cartNoteCheckbox.checked;

    if (cartNoteCheckbox.checked) {
      return;
    }

    const previousNote = noteInput.value;
    const checkoutButton = this.querySelector('[name="checkout"]');
    noteInput.value = "";
    checkoutButton.disabled = true;

    try {
      const response = await fetch(`${window.routes.cartUpdateUrl}.js`, {
        body: JSON.stringify({ note: "" }),
        credentials: "same-origin",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        throw new Error("Unable to clear the cart note.");
      }
    } catch (error) {
      cartNoteCheckbox.checked = true;
      noteInput.disabled = false;
      noteInput.value = previousNote;
      console.error(error);
    } finally {
      checkoutButton.disabled = false;
    }
  }

  keyboardHandler(event) {
    const quantityInput = event.target.closest(".quantity-selector__value");

    if (
      event.type === "keyup" &&
      quantityInput &&
      !quantityInput.closest(".quantity-selector--product")
    ) {
      quantityInput.setAttribute("size", Math.max(quantityInput.value.length, 2));
    } else if (event.type === "keydown" && event.key === "Enter" && quantityInput) {
      event.preventDefault();
    }
  }

  async cartEventHandler(event) {
    await this._onCartRefresh(event.type === "cart:refresh" ? event : undefined);
  }

  async _updateQuantity(event, target) {
    event.preventDefault();

    const parsedQuantity =
      target.tagName === "INPUT"
        ? parseInt(target.value)
        : parseInt(target.getAttribute("data-quantity"));

    if (window.theme.cartType === "page") {
      window.location.href = target.hasAttribute("data-href")
        ? target.getAttribute("data-href")
        : `${window.routes.cartChangeUrl}?line=${target.getAttribute(
            "data-line",
          )}&quantity=${parsedQuantity}`;
      return;
    }

    const response = await fetch(`${window.routes.cartChangeUrl}.js`, {
      body: JSON.stringify({
        line: target.getAttribute("data-line"),
        quantity: parsedQuantity,
      }),
      credentials: "same-origin",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    });
    const content = await response.json();

    document.dispatchEvent(new CustomEvent("addToCartFinish", { detail: content }));
    document.dispatchEvent(new CustomEvent("cart:updated", { detail: { data: content } }));
    this.itemCount = content.item_count;
    await this._rerender(false);
  }

  async _rerender(scrollToTop = true) {
    const url = `${window.routes.cartUrl}?section_id=${this.getAttribute("data-section-id")}`;

    const response = await fetch(url, {
      credentials: "same-origin",
      method: "GET",
      headers: {
        "Cache-Control": "no-cache",
      },
    });
    const html = await response.text();
    const tempElement = document.createElement("div");
    tempElement.innerHTML = html;

    const newCart = tempElement.querySelector("main-cart");
    this.itemCount = parseInt(newCart.getAttribute("data-item-count")) || 0;
    this.setAttribute("data-item-count", this.itemCount);
    this.innerHTML = newCart.innerHTML;

    const headerCartCount = document.querySelector(".header__cart-count");
    if (headerCartCount) {
      headerCartCount.textContent = this.itemCount;
      headerCartCount.classList.toggle("cart-empty", this.itemCount < 1);
    }

    if (scrollToTop) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    this.dispatchEvent(new CustomEvent("cart:rerendered", { bubbles: true }));
  }

  async _onCartRefresh(event) {
    const scrollToTop = event?.detail ? event.detail.scrollToTop : true;
    await this._rerender(scrollToTop);
  }
}

customElements.define("main-cart", MainCart);

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.content = this.querySelector(".announcement-bar__content");
    this.cartEventHandler = this.cartEventHandler.bind(this);
    this.events = [
      {
        el: document,
        type: "addToCartFinish",
        callback: this.cartEventHandler,
      },
      {
        el: document,
        type: "product:added",
        callback: this.cartEventHandler,
      },
      {
        el: document,
        type: "cart:refresh",
        callback: this.cartEventHandler,
      },
    ];
  }

  connectedCallback() {
    handleEventListeners(this.events, "add");
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
  }

  async cartEventHandler(event) {
    let cart = event.type === "addToCartFinish" ? event.detail : null;

    if (!this.isCart(cart)) {
      cart = await this.fetchCart();
    }

    if (this.isCart(cart)) {
      this.render(cart);
    }
  }

  isCart(cart) {
    return Number.isFinite(cart?.item_count) && Number.isFinite(cart?.total_price);
  }

  async fetchCart() {
    try {
      const response = await fetch(`${window.routes.cartUrl}.js`, {
        credentials: "same-origin",
        headers: {
          "Cache-Control": "no-cache",
        },
      });

      if (!response.ok) {
        throw new Error(`Unable to fetch cart: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  render(cart) {
    const threshold = parseInt(this.dataset.freeShippingThreshold, 10);

    if (!this.content || !Number.isFinite(threshold)) return;

    if (cart.item_count < 1) {
      this.renderState("empty");
      this.style.removeProperty("background-color");
      this.style.removeProperty("color");
      return;
    }

    this.style.backgroundColor = this.dataset.activeBackground;
    this.style.color = this.dataset.activeColor;

    if (cart.total_price >= threshold) {
      this.renderState("eligible");
      return;
    }

    this.renderState("remaining", threshold - cart.total_price);
  }

  renderState(state, remainingAmount) {
    const template = this.querySelector(`template[data-announcement-state="${state}"]`);

    if (!template) return;

    const content = template.content.cloneNode(true);
    const remainingAmountElement = content.querySelector("[data-remaining-amount]");

    if (remainingAmountElement) {
      remainingAmountElement.textContent = window.currency.formatMoney(
        remainingAmount,
        window.theme.moneyFormat,
      );
    }

    this.content.replaceChildren(content);
  }
}

customElements.define("announcement-bar", AnnouncementBar);
